/* ------------------------------------------------------------------
   countryFlags.ts
   Maps a country NAME (as returned by /v2/Dependent/GetCountry, which
   only sends country_id + name) to its ISO-3166 alpha-2 code, and
   resolves that code to a flag image URL.

   Flags are bundled LOCALLY from the `flag-icons` package (SVG, so they
   render identically on Windows — unlike flag emoji, which Windows shows
   as letter pairs — and need no external / CDN request). Webpack's
   require.context pulls every 4x3 flag SVG into the build and gives each
   a hashed asset URL. Names are matched case/spacing/punctuation-
   insensitively, with a few aliases for common alternate spellings.
   Unknown names simply return "" so the caller can fall back to no flag.
   ------------------------------------------------------------------ */

const NAME_TO_ISO: Record<string, string> = {
  afghanistan: "af",
  alandislands: "ax",
  albania: "al",
  algeria: "dz",
  americansamoa: "as",
  andorra: "ad",
  angola: "ao",
  anguilla: "ai",
  antiguaandbarbuda: "ag",
  argentina: "ar",
  armenia: "am",
  aruba: "aw",
  australia: "au",
  austria: "at",
  azerbaijan: "az",
  bahamas: "bs",
  bahrain: "bh",
  bangladesh: "bd",
  barbados: "bb",
  belarus: "by",
  belgium: "be",
  belize: "bz",
  benin: "bj",
  bermuda: "bm",
  bhutan: "bt",
  bolivia: "bo",
  bosniaandherzegovina: "ba",
  botswana: "bw",
  brazil: "br",
  britishindianoceanterritory: "io",
  britishvirginislands: "vg",
  brunei: "bn",
  bruneidarussalam: "bn",
  bulgaria: "bg",
  burkinafaso: "bf",
  burundi: "bi",
  caboverde: "cv",
  capeverde: "cv",
  cambodia: "kh",
  cameroon: "cm",
  canada: "ca",
  caymanislands: "ky",
  centralafricanrepublic: "cf",
  chad: "td",
  chile: "cl",
  china: "cn",
  colombia: "co",
  comoros: "km",
  congo: "cg",
  congobrazzaville: "cg",
  congokinshasa: "cd",
  democraticrepublicofthecongo: "cd",
  cookislands: "ck",
  costarica: "cr",
  cotedivoire: "ci",
  ivorycoast: "ci",
  croatia: "hr",
  cuba: "cu",
  curacao: "cw",
  cyprus: "cy",
  czechia: "cz",
  czechrepublic: "cz",
  denmark: "dk",
  djibouti: "dj",
  dominica: "dm",
  dominicanrepublic: "do",
  ecuador: "ec",
  egypt: "eg",
  elsalvador: "sv",
  equatorialguinea: "gq",
  eritrea: "er",
  estonia: "ee",
  eswatini: "sz",
  swaziland: "sz",
  ethiopia: "et",
  falklandislands: "fk",
  faroeislands: "fo",
  fiji: "fj",
  finland: "fi",
  france: "fr",
  frenchguiana: "gf",
  frenchpolynesia: "pf",
  gabon: "ga",
  gambia: "gm",
  georgia: "ge",
  germany: "de",
  ghana: "gh",
  gibraltar: "gi",
  greece: "gr",
  greenland: "gl",
  grenada: "gd",
  guadeloupe: "gp",
  guam: "gu",
  guatemala: "gt",
  guernsey: "gg",
  guinea: "gn",
  guineabissau: "gw",
  guyana: "gy",
  haiti: "ht",
  honduras: "hn",
  hongkong: "hk",
  hungary: "hu",
  iceland: "is",
  india: "in",
  indonesia: "id",
  iran: "ir",
  iraq: "iq",
  ireland: "ie",
  isleofman: "im",
  israel: "il",
  italy: "it",
  jamaica: "jm",
  japan: "jp",
  jersey: "je",
  jordan: "jo",
  kazakhstan: "kz",
  kenya: "ke",
  kiribati: "ki",
  kosovo: "xk",
  kuwait: "kw",
  kyrgyzstan: "kg",
  laos: "la",
  latvia: "lv",
  lebanon: "lb",
  lesotho: "ls",
  liberia: "lr",
  libya: "ly",
  liechtenstein: "li",
  lithuania: "lt",
  luxembourg: "lu",
  macao: "mo",
  macau: "mo",
  madagascar: "mg",
  malawi: "mw",
  malaysia: "my",
  maldives: "mv",
  mali: "ml",
  malta: "mt",
  marshallislands: "mh",
  martinique: "mq",
  mauritania: "mr",
  mauritius: "mu",
  mayotte: "yt",
  mexico: "mx",
  micronesia: "fm",
  moldova: "md",
  monaco: "mc",
  mongolia: "mn",
  montenegro: "me",
  montserrat: "ms",
  morocco: "ma",
  mozambique: "mz",
  myanmar: "mm",
  burma: "mm",
  namibia: "na",
  nauru: "nr",
  nepal: "np",
  netherlands: "nl",
  newcaledonia: "nc",
  newzealand: "nz",
  nicaragua: "ni",
  niger: "ne",
  nigeria: "ng",
  niue: "nu",
  northkorea: "kp",
  northmacedonia: "mk",
  macedonia: "mk",
  norway: "no",
  oman: "om",
  pakistan: "pk",
  palau: "pw",
  palestine: "ps",
  panama: "pa",
  papuanewguinea: "pg",
  paraguay: "py",
  peru: "pe",
  philippines: "ph",
  poland: "pl",
  portugal: "pt",
  puertorico: "pr",
  qatar: "qa",
  reunion: "re",
  romania: "ro",
  russia: "ru",
  russianfederation: "ru",
  rwanda: "rw",
  saintkittsandnevis: "kn",
  saintlucia: "lc",
  saintvincentandthegrenadines: "vc",
  samoa: "ws",
  sanmarino: "sm",
  saotomeandprincipe: "st",
  saudiarabia: "sa",
  senegal: "sn",
  serbia: "rs",
  seychelles: "sc",
  sierraleone: "sl",
  singapore: "sg",
  sintmaarten: "sx",
  slovakia: "sk",
  slovenia: "si",
  solomonislands: "sb",
  somalia: "so",
  southafrica: "za",
  southkorea: "kr",
  koreasouth: "kr",
  republicofkorea: "kr",
  southsudan: "ss",
  spain: "es",
  srilanka: "lk",
  sudan: "sd",
  suriname: "sr",
  sweden: "se",
  switzerland: "ch",
  syria: "sy",
  taiwan: "tw",
  tajikistan: "tj",
  tanzania: "tz",
  thailand: "th",
  timorleste: "tl",
  easttimor: "tl",
  togo: "tg",
  tonga: "to",
  trinidadandtobago: "tt",
  tunisia: "tn",
  turkey: "tr",
  turkiye: "tr",
  turkmenistan: "tm",
  turksandcaicosislands: "tc",
  tuvalu: "tv",
  uganda: "ug",
  ukraine: "ua",
  unitedarabemirates: "ae",
  uae: "ae",
  unitedkingdom: "gb",
  uk: "gb",
  greatbritain: "gb",
  england: "gb",
  unitedstates: "us",
  unitedstatesofamerica: "us",
  usa: "us",
  us: "us",
  uruguay: "uy",
  uzbekistan: "uz",
  vanuatu: "vu",
  vaticancity: "va",
  venezuela: "ve",
  vietnam: "vn",
  yemen: "ye",
  zambia: "zm",
  zimbabwe: "zw",
};

const normalize = (name: string): string =>
  (name || "").toLowerCase().replace(/[^a-z]/g, "");

/* Bundle every 4x3 flag SVG from flag-icons and map iso-code -> asset URL.
   require.context is a webpack (Create React App) build-time feature. */
const FLAG_URLS: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  try {
    // @ts-ignore  require.context is provided by webpack, not typed here
    const ctx = (require as any).context("flag-icons/flags/4x3", false, /\.svg$/);
    ctx.keys().forEach((key: string) => {
      const iso = key.replace(/^\.\//, "").replace(/\.svg$/, "").toLowerCase();
      const mod = ctx(key);
      map[iso] = (mod && mod.default) || mod;
    });
  } catch (e) {
    /* non-webpack env (e.g. tests) — leave map empty, callers fall back to no flag */
  }
  return map;
})();

/** ISO-3166 alpha-2 code for a country name, or "" if unknown. */
export const getCountryIso = (name: string): string => NAME_TO_ISO[normalize(name)] || "";

/** Local (bundled) flag SVG url for an ISO alpha-2 code, or "" if unavailable. */
export const getFlagUrl = (iso: string): string => (iso && FLAG_URLS[iso.toLowerCase()]) || "";

/** Convenience: flag url straight from a country name. */
export const getFlagUrlByName = (name: string): string => getFlagUrl(getCountryIso(name));
